<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useStore } from 'vuex'

import { X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { MapCanvas, MapLayers, MapDraw, DialogCreate } from '@/components/map'
import BasemapStack from '@/components/maps/BasemapStack.vue'
import { listInstances, getInstance } from '@/utils/mapbox/instanceRegistry'
import { useMapSync } from '@/composables/useMapSync'

const store = useStore()
const orgId = computed(() => store.getters['org/getSelected'])

const tiles = computed<string[]>(() => store.getters['split/getTiles'](orgId.value))
const arrangement = computed<string>(() => store.getters['split/getArrangement'](orgId.value))
const dividers = computed(() => store.getters['split/getDividers'](orgId.value))
const maps = computed<any[]>(() => store.getters['maps/getAllMaps'](orgId.value))
const defaultMap = computed<any>(() => store.getters['maps/getDefaultMap'](orgId.value))

// MapGrid is mounted for every layers-tab route; MapsPanel only for the list ones.
const mapsLoaded = ref(false)
const fetchAndSync = async () => {
  if (!orgId.value) return
  mapsLoaded.value = false
  try {
    await store.dispatch('layers/fetchOrgLayers', { orgId: orgId.value })
    await store.dispatch('maps/fetchMaps', { orgId: orgId.value })
  } finally {
    mapsLoaded.value = true
  }
}
watch(orgId, fetchAndSync, { immediate: true })
watch(() => store.getters['layers/getOrgLayers'](orgId.value)?.length, () => {
  if (orgId.value) store.dispatch('maps/fetchMaps', { orgId: orgId.value })
})

// Only for an org that genuinely has no maps. Rendering it while the fetch is still
// in flight would mount a canvas that the real map id then replaces, and that
// remount tears down a live Mapbox instance mid-frame.
const FALLBACK_MAP = { id: '__default__', name: 'Map', basemap: 'outdoors', color: '#64748b', layerCodes: [] }

// Seed the Default Map as the first visible tile once maps load. After that, `tiles`
// is the source of truth: it may legitimately be empty (user hid every map).
watch(defaultMap, (def) => {
  if (def) store.dispatch('split/ensureTiles', { orgId: orgId.value })
}, { immediate: true })

// The map area is a grid of the currently-visible maps (the tiles). Only the
// pre-seed window renders the fallback; once maps exist, tiles drives everything.
// Tiles are filtered to maps that still resolve — a stale id (deleted map, restored
// URL, org switch) would otherwise render as a blank pane with no explanation.
const effectiveTiles = computed<string[]>(() => {
  const live = tiles.value.filter(id => maps.value.some(m => m.id === id))
  if (live.length) return live
  if (maps.value.length || !mapsLoaded.value) return []
  return [FALLBACK_MAP.id]
})
const n = computed(() => effectiveTiles.value.length)
const isSplit = computed(() => n.value > 1)
const mapAt = (i: number) => effectiveTiles.value[i]
const mapObj = (i: number) => {
  const id = mapAt(i)
  return maps.value.find(m => m.id === id) || (id === FALLBACK_MAP.id ? FALLBACK_MAP : undefined)
}
const mapName = (i: number) => mapObj(i)?.name || ''
const layersFor = (mapId: string) => store.getters['maps/getMapLayers'](orgId.value, mapId)
const cellIndexes = computed(() => Array.from({ length: n.value }, (_, k) => k))

const removeTile = (mapId: string) => store.dispatch('split/removeTileMap', { orgId: orgId.value, mapId })

const containerRef = ref<HTMLElement | null>(null)

// --- Cell geometry (fractions) from tile count + arrangement -------------------
const slotRects = computed(() => {
  const { x, y } = dividers.value
  const count = n.value
  if (count <= 1) return [{ left: 0, top: 0, w: 1, h: 1 }]
  if (count === 2) {
    return arrangement.value === 'tb'
      ? [ { left: 0, top: 0, w: 1, h: y }, { left: 0, top: y, w: 1, h: 1 - y } ]
      : [ { left: 0, top: 0, w: x, h: 1 }, { left: x, top: 0, w: 1 - x, h: 1 } ]
  }
  if (count === 3) {
    switch (arrangement.value) {
      case 'right':  // two stacked left + full-right
        return [ { left: 0, top: 0, w: x, h: y }, { left: 0, top: y, w: x, h: 1 - y }, { left: x, top: 0, w: 1 - x, h: 1 } ]
      case 'top':    // full-top + two below
        return [ { left: 0, top: 0, w: 1, h: y }, { left: 0, top: y, w: x, h: 1 - y }, { left: x, top: y, w: 1 - x, h: 1 - y } ]
      case 'bottom': // two on top + full-bottom
        return [ { left: 0, top: 0, w: x, h: y }, { left: x, top: 0, w: 1 - x, h: y }, { left: 0, top: y, w: 1, h: 1 - y } ]
      case 'left':   // full-left + two stacked right
      default:
        return [ { left: 0, top: 0, w: x, h: 1 }, { left: x, top: 0, w: 1 - x, h: y }, { left: x, top: y, w: 1 - x, h: 1 - y } ]
    }
  }
  return [
    { left: 0, top: 0, w: x, h: y },
    { left: x, top: 0, w: 1 - x, h: y },
    { left: 0, top: y, w: x, h: 1 - y },
    { left: x, top: y, w: 1 - x, h: 1 - y },
  ]
})
const rectStyle = (i: number) => {
  const r = slotRects.value[i]
  return { left: `${r.left * 100}%`, top: `${r.top * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` }
}

// The grid box starts one bleed underneath the sidebar panel card, so tiles on
// the left edge must push their on-map chip back out past it.
const chipLeft = (i: number) =>
  slotRects.value[i]?.left === 0 ? 'calc(0.5rem + var(--map-bleed, 0px))' : '0.5rem'

// Divider lines, sized to the real shared edge for each arrangement. A vertical
// line sits at x (top/bottom = fractional insets); a horizontal at y (left/right).
const verticalLine = computed(() => {
  if (n.value < 2) return null
  const { x, y } = dividers.value
  const a = arrangement.value
  if (n.value === 2) return a === 'tb' ? null : { top: 0, bottom: 0, x }
  if (n.value === 3) {
    if (a === 'top') return { top: y, bottom: 0, x }      // only bottom row
    if (a === 'bottom') return { top: 0, bottom: 1 - y, x } // only top row
    if (a === 'left' || a === 'right') return { top: 0, bottom: 0, x }
    return { top: 0, bottom: 0, x }
  }
  return { top: 0, bottom: 0, x } // quad
})
const horizontalLine = computed(() => {
  if (n.value < 2) return null
  const { x, y } = dividers.value
  const a = arrangement.value
  if (n.value === 2) return a === 'tb' ? { left: 0, right: 0, y } : null
  if (n.value === 3) {
    if (a === 'left') return { left: x, right: 0, y }       // only right column
    if (a === 'right') return { left: 0, right: 1 - x, y }  // only left column
    if (a === 'top' || a === 'bottom') return { left: 0, right: 0, y }
    return { left: x, right: 0, y }
  }
  return { left: 0, right: 0, y } // quad
})
// The 2-D grab handle sits at the divider intersection for 3+ maps.
const showCrossHandle = computed(() => n.value >= 3)

// --- Camera sync ---------------------------------------------------------------
const { rebuild } = useMapSync()
const instanceEpoch = ref(0)
const onInstanceChanged = () => { instanceEpoch.value++ }

watch(
  () => [
    effectiveTiles.value.join(','),
    effectiveTiles.value.map(id => `${store.getters['split/isSyncEnabled'](id)}`).join(','),
    instanceEpoch.value,
  ],
  () => {
    nextTick(() => {
      const syncIds = effectiveTiles.value.filter(id => store.getters['split/isSyncEnabled'](id))
      rebuild(listInstances(syncIds))
    })
  },
  { immediate: true, deep: true }
)

// --- Publish the first visible tile as geo/getMap + geo/getDraw so search /
// popups / settings / FeatureSelection operate on a live map. Draws are kept in
// a plain Map (never in reactive state — same reasoning as instanceRegistry). --
const tileDraws = new Map<string, any>()
const drawEpoch = ref(0)
const onDrawReady = (mapId: string, draw: any) => {
  tileDraws.set(mapId, draw)
  drawEpoch.value++
}
const focusedTile = computed<string | null>(() => store.getters['split/getFocusedTile'])
const commitActive = () => {
  const focused = focusedTile.value
  const id = (focused && effectiveTiles.value.includes(focused)) ? focused : effectiveTiles.value[0]
  const inst = id ? getInstance(id) : undefined
  if (inst) {
    store.commit('geo/SET_MAP', inst)
    store.dispatch('geo/mapReady', true)
  }
  const draw = id ? tileDraws.get(id) : undefined
  store.commit('geo/SET_DRAW', draw || null)
}
watch([() => effectiveTiles.value[0], focusedTile, instanceEpoch, drawEpoch], () => nextTick(commitActive), { immediate: true })

// --- Draggable dividers --------------------------------------------------------
const startDrag = (axis: 'x' | 'y') => (e: PointerEvent) => {
  e.preventDefault()
  const el = containerRef.value
  if (!el) return
  const move = (ev: PointerEvent) => {
    const rect = el.getBoundingClientRect()
    store.dispatch('split/setDividers', {
      orgId: orgId.value,
      x: axis === 'x' ? (ev.clientX - rect.left) / rect.width : null,
      y: axis === 'y' ? (ev.clientY - rect.top) / rect.height : null,
    })
  }
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

// 2-D drag: move both dividers at once (the intersection handle for 3+ maps).
const startDrag2D = (e: PointerEvent) => {
  e.preventDefault()
  const el = containerRef.value
  if (!el) return
  const move = (ev: PointerEvent) => {
    const rect = el.getBoundingClientRect()
    store.dispatch('split/setDividers', {
      orgId: orgId.value,
      x: (ev.clientX - rect.left) / rect.width,
      y: (ev.clientY - rect.top) / rect.height,
    })
  }
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

onUnmounted(() => {
  rebuild([])
  const globalDraw = store.getters['geo/getDraw']
  if (globalDraw && [...tileDraws.values()].includes(globalDraw)) {
    store.commit('geo/SET_DRAW', null)
  }
})
</script>

<template>
  <div ref="containerRef" class="absolute inset-0 bg-slate-200">
    <!-- Key each cell by its mapId (not the slot index) so that when tiles are
         reordered/removed Vue MOVES the surviving tile's canvas instead of
         destroying + recreating it. Recreating races the registry (same mapId
         key) and drops the live instance → a gray, dead tile. -->
    <div
      v-for="i in cellIndexes"
      :key="mapAt(i)"
      class="absolute p-0.5"
      :style="rectStyle(i)"
    >
      <div
        class="absolute inset-0.5 overflow-hidden"
        :class="isSplit ? 'rounded-lg border border-border/60 bg-background/60' : ''"
      >
        <MapCanvas
          v-if="mapObj(i)"
          :key="mapAt(i)"
          :map="mapObj(i)"
          @ready="onInstanceChanged"
          v-slot="{ instance }"
        >
          <MapLayers
            :map="instance"
            :layers="layersFor(mapAt(i))"
            :org-id="orgId"
            :map-id="mapAt(i)"
            :ready="true"
          />
          <!-- Combined window-manager pill: name + close (only when tiling) and
               this tile's basemap settings thumbnail (flush, full-height) — one
               element offset below the global top bar so it never collides with
               search/breadcrumbs. -->
          <div
            class="absolute top-12 z-10 pointer-events-auto flex items-stretch h-9 rounded-md bg-background/85 backdrop-blur-sm shadow-sm border border-border/50 overflow-hidden max-w-[80%]"
            :style="{ left: chipLeft(i) }"
          >
            <div v-if="isSplit" class="flex items-center gap-1.5 pl-2 pr-1">
              <span class="w-2.5 h-2.5 rounded-sm flex-shrink-0" :style="{ backgroundColor: mapObj(i)?.color }" />
              <span class="text-xs font-medium truncate">{{ mapName(i) }}</span>
              <Button size="sm" variant="ghost" class="w-5 h-5 p-0 rounded flex-shrink-0" @click="removeTile(mapAt(i))">
                <X class="w-3.5 h-3.5" />
              </Button>
            </div>
            <BasemapStack
              :instance="instance"
              :map="mapObj(i)"
              :org-id="orgId"
            />
          </div>
          <MapDraw :map="instance" :global="false" @ready="onDrawReady(mapAt(i), $event)" v-slot="{ draw }">
            <div class="absolute z-30 bottom-16 right-10 py-2 px-3">
              <div class="flex items-end justify-end">
                <DialogCreate
                  v-if="draw"
                  :is-layers-view="true"
                  :map="instance"
                  :draw="draw"
                  :target-map-id="mapAt(i)"
                />
              </div>
            </div>
          </MapDraw>
        </MapCanvas>
      </div>
    </div>

    <!-- Blank canvas: every map's eye is off. -->
    <div v-if="!n && mapsLoaded" class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
      No maps shown — turn one on in the map list
    </div>

    <!-- Divider drag handles -->
    <div
      v-if="verticalLine"
      class="absolute w-3 -ml-1.5 z-20 cursor-col-resize hover:bg-primary/20"
      :style="{ left: `${verticalLine.x * 100}%`, top: `${verticalLine.top * 100}%`, bottom: `${verticalLine.bottom * 100}%` }"
      @pointerdown.stop.prevent="startDrag('x')($event)"
    />
    <div
      v-if="horizontalLine"
      class="absolute h-3 -mt-1.5 z-20 cursor-row-resize hover:bg-primary/20"
      :style="{ top: `${horizontalLine.y * 100}%`, left: `${horizontalLine.left * 100}%`, right: `${horizontalLine.right * 100}%` }"
      @pointerdown.stop.prevent="startDrag('y')($event)"
    />

    <!-- 2-D grab handle at the divider intersection (3+ maps) -->
    <div
      v-if="showCrossHandle"
      class="absolute z-20 w-5 h-5 -ml-2.5 -mt-2.5 rounded-full bg-primary border-2 border-white shadow-lg cursor-move hover:scale-110 transition-transform"
      :style="{ left: `${dividers.x * 100}%`, top: `${dividers.y * 100}%` }"
      @pointerdown="startDrag2D"
    />
  </div>
</template>
